import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ElementRef,
    Host,
    Input,
    OnDestroy,
    Optional,
    SkipSelf,
    ViewChild,
} from "@angular/core";
import { ControlContainer, FormControl } from "@angular/forms";
import { SpsIcon } from "@spscommerce/ds-shared";
import { AttributePresenceInput } from "@spscommerce/ng-utils";
import { onNextTick } from "@spscommerce/utils";
import autobind from "autobind-decorator";

import { SpsFormComponent } from "../forms/sps-form-component.abstract.component";
import { SpsTooltipComponent } from "../tooltip/sps-tooltip.component";

@Component({
    selector: "sps-label-error",
    template: "<div><ng-content></ng-content></div>"
})
export class SpsLabelErrorComponent {}

@Component({
    selector: "sps-label-help",
    template: "<ng-content></ng-content>"
})
export class SpsLabelHelpComponent {
    @Input() icon = SpsIcon.QUESTION_CIRCLE;
    @Input() iconColor = "blue300";
}

@Component({
    selector: "sps-label",
    templateUrl: "./sps-label.component.html"
})
export class SpsLabelComponent extends SpsFormComponent implements AfterViewInit, OnDestroy {
    static readonly displayName = "sps-label";
    static readonly props = {
        for: { type: "string", required: true },
        description: "string",
        stronglySuggested: "boolean",
    };

    @Input() for: string;
    @Input() description: string;
    @Input() @AttributePresenceInput() stronglySuggested: boolean;

    @ViewChild("helpTooltip", { static: true }) helpTooltip: SpsTooltipComponent;
    @ViewChild("errorTooltip", { static: true }) errorTooltip: SpsTooltipComponent;
    @ViewChild("suggestedTooltip", { static: true }) suggestedTooltip: SpsTooltipComponent;

    @ContentChild(SpsLabelErrorComponent, { static: false }) errorContent: SpsLabelErrorComponent;
    @ContentChild(SpsLabelHelpComponent, { static: false }) helpContent: SpsLabelHelpComponent;

    @ViewChild("label", { static: true }) label: ElementRef;
    @ViewChild("helpIcon", { static: false }) helpIcon: ElementRef;

    _forElement: ElementRef;
    private forElementObserver: MutationObserver;

    get priorityTooltip() {
        return this.isInErrorState
            ? this.errorTooltip
            : this.helpContent
                ? this.helpTooltip
                : this.suggestedTooltip;
    }

    get requiredFieldError() {
        return this.isInErrorState && this._control && this._control.errors && this._control.errors.required;
    }

    get customErrorMessages() {
        const errs = [];
        if (this._control && this._control.errors) {
            for (const key of Object.keys(this._control.errors)) {
                const error = this._control.errors[key];
                if (error && error.hasOwnProperty("spsLabelErrorMessage")) {
                    errs.push(error.spsLabelErrorMessage);
                }
            }
        }
        return errs;
    }

    constructor(
        @Optional()
        @Host()
        @SkipSelf()
        private ctrlContainer: ControlContainer,
        private elementRef: ElementRef,
        private changeDetector: ChangeDetectorRef,
    ) {
        super();
    }

    ngAfterViewInit() {
        if (typeof this.for === "string" && this.for) {
            if (this.ctrlContainer) {
                const control = <FormControl>this.ctrlContainer.control.get(this.for);
                if (control) {
                    control.statusChanges.subscribe(this.updateTooltipStatus);
                    // Assigning this immediately can cause "changed after checked" error
                    onNextTick(() => {
                        this._control = control;
                        this.changeDetector.markForCheck();
                    });
                }
            }

            const controlHTMLElement =
                this.elementRef.nativeElement.parentElement.querySelector(
                    `[formControlName="${this.for}"]`
                ) ||
                this.elementRef.nativeElement.parentElement.querySelector(`[formGroupName="${this.for}"]`);

            if (controlHTMLElement) {
                onNextTick(() => {
                    this._forElement = new ElementRef(controlHTMLElement);
                    this.forElementObserver = new MutationObserver(this.updateTooltipStatus);
                    this.forElementObserver.observe(this._forElement.nativeElement, {
                        attributes: true,
                        attributeFilter: ["class"]
                    });
                });
            }
        }
    }

    ngOnDestroy() {
        if (this.forElementObserver) {
            this.forElementObserver.disconnect();
        }
    }

    hideTooltips(except?: SpsTooltipComponent) {
        if (
            !except &&
            this._forElement &&
            this._forElement.nativeElement.classList.contains("sps-form-control--focused")
        ) {
            return;
        }
        if (except !== this.helpTooltip) {
            this.helpTooltip.hide();
        }
        if (except !== this.errorTooltip) {
            this.errorTooltip.hide();
        }
        if (except !== this.suggestedTooltip) {
            this.suggestedTooltip.hide();
        }
    }

    clickSuggestedIcon(event: MouseEvent) {
        event.preventDefault();
        this.hideTooltips(this.suggestedTooltip);
        this.suggestedTooltip.show();
    }

    @autobind
    updateTooltipStatus() {
        if (
            this._forElement &&
            this._forElement.nativeElement.classList.contains("sps-form-control--focused")
        ) {
            this.hideTooltips(this.priorityTooltip);
            this.priorityTooltip.show();
        } else {
            this.hideTooltips();
        }
    }
}
